// @flow
import * as React from "react";

import { Col, Layout, Row, Segmented, Typography } from "antd";
import { MoonOutlined, SunOutlined } from "@ant-design/icons";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { useTheme } from "hooks/useTheme";

import AuthBrandLogo from "./AuthBrandLogo";

import "./AuthLayout.scss";

const AUTH_TABS = {
  "/login": "login",
  "/signup": "signup",
};

const SEGMENTED_OPTIONS = [
  { label: "Log in", value: "login" },
  { label: "Become a partner", value: "signup" },
];

function AuthLayout(): React.Node {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();

  const year = new Date().getFullYear();
  const authTab = AUTH_TABS[location.pathname] || "login";

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const handleAuthTabChange = (value: string) => {
    if (value === "login") {
      navigate("/login");
      return;
    }

    navigate("/signup");
  };

  return (
    <Layout className={`AuthLayout AuthLayout--${authTab}`}>
      <Row className="AuthLayout__row" wrap={false}>
        <Col className="AuthLayout__brand" lg={12} xs={0}>
          <AuthBrandLogo />

          <div className="AuthLayout__brandContent">
            <Typography.Text className="AuthLayout__brandOverline">
              Performance marketing • iGaming & dating
            </Typography.Text>

            <Typography.Title level={1} className="AuthLayout__brandHeadline">
              From creative ideas to <span className="AuthLayout__brandHighlight">big results.</span>
            </Typography.Title>

            <Typography.Text className="AuthLayout__brandSubtext">
              Unleash a vortex of tech-savvy solutions, leading-hand specialists, and impressive results
            </Typography.Text>
          </div>
        </Col>

        <Col className="AuthLayout__form" lg={12} xs={24}>
          <div className="AuthLayout__formHeader">
            <div className="AuthLayout__mobileBrandLogo">
              <AuthBrandLogo />
            </div>

            <button
              type="button"
              className="AuthLayout__themeToggle"
              aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              onClick={toggleTheme}>
              {theme === "dark" ? <SunOutlined /> : <MoonOutlined />}
            </button>
          </div>

          <div className="AuthLayout__formBody">
            <div className="AuthLayout__formInner">
              <div className="AuthLayout__segmented">
                <Segmented block options={SEGMENTED_OPTIONS} value={authTab} onChange={handleAuthTabChange} />
              </div>

              <Outlet />
            </div>
          </div>

          <div className="AuthLayout__formFooter">
            <Typography.Text className="AuthLayout__copyright">
              &copy; {year} Vortex. All rights reserved.
            </Typography.Text>

            <div className="AuthLayout__footerLinks">
              <Typography.Link
                className="AuthLayout__footerLink"
                href={`${process.env.PUBLIC_URL || ""}/terms-and-conditions.html`}
                target="_blank"
                rel="noopener noreferrer">
                Terms
              </Typography.Link>
            </div>
          </div>
        </Col>
      </Row>
    </Layout>
  );
}

export default AuthLayout;
